import { useCollection } from "@/features/collections/useCollection";
import {
  CollectionToolbar,
  CollectionPagination,
} from "@/features/collections/CollectionControls";
import { useEffect, useMemo, useState } from "react";
import { ClipboardList } from "lucide-react";
import { useDeepLinkTarget } from "./useDeepLinkTarget";
import type {
  CoachingClip,
  CoachingNote,
  CoachingPlaylist,
  FeedbackBundle,
  MatchSummary,
} from "@/lib/types";
import { FeedbackPlayerModal } from "@/features/playback/FeedbackPlayerModal";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { useFeedbackReview } from "./hooks/useFeedbackReview";
import { useToast } from "@/components/ui/toast";
import { errorMessage } from "@/lib/api";
import {
  buildFeedbackReviewItems,
  FEEDBACK_REVIEW_FILTER_LABELS,
  FEEDBACK_REVIEW_FILTERS,
  filterReviewItem,
  type FeedbackReviewFilter,
  type FeedbackReviewItem,
} from "./feedbackReviewItems";
import {
  noteDisplayTitle,
  shortDateLabel,
  summarySections,
  summaryVisibilityLabel,
} from "./feedbackHelpers";
import { boardHasContent } from "./components/BoardChip";
import { FeedbackMediaThumbnail } from "@/features/coach-review/components/FeedbackMediaThumbnail";
import { TacticalBoardThumbnail } from "@/features/canvas/TacticalBoardThumbnail";
import { PlayerResponseChips } from "./components/PlayerResponseChips";
import { buildPlayerResponseIndex } from "./responseSignals";

/**
 * Unified Review feed — PR 2 of the Feedback Player UX redesign.
 *
 * Replaces the PR 1 thin wrapper. Notes, clips, and match recaps
 * match summaries are normalized through `feedbackReviewItems.ts` and
 * rendered as a single sortable, filterable card list with
 * player-friendly type pills, copy ("What to do next — …"), and
 * primary actions ("Watch" / "Review" / "Read" /
 * "Mark done").
 *
 * Modal wiring reuses the existing M5 `FeedbackPlayerModal` so every
 * notes and clips open in the same focused player; summaries
 * inline-expand on the card (they don't have a media counterpart).
 *
 * Filter chips: All · New · Video · Tactical · Summaries
 * · Reviewed. Chips can scroll horizontally on mobile (the mockup's
 * "filter chips may scroll horizontally" allowance from Phase 8).
 *
 * Privacy: every templated field is player-facing
 * (`what_to_do_next`, `player_summary`, clip `description`,
 * summary recap sections). `coach_private_note`
 * is never read here — covered by `feedbackReviewItems.test.ts` and
 * by the `feedback-privacy.spec.js` DOM scan.
 */

export type FeedbackReviewTabProps = {
  bundle: FeedbackBundle | undefined;
  reviewedNoteIds: Set<number>;
  reviewedPlaylistIds: Set<number>;
  /** PR 7: clip review state — empty set for pre-PR 7 bundles
   * (clips stay `new` until marked done). */
  reviewedClipIds?: Set<number>;
  /** Phase B: match-recap read state — empty set for pre-Phase-B
   * bundles (recaps stay `new` until marked read). */
  reviewedSummaryIds?: Set<number>;
  /** PR 2: deep-link to a specific match summary card. The Overview
   * surface passes `?summary=<id>` so the Review tab can land the
   * user on the right recap without an extra click. When set, the
   * tab auto-selects the Summaries filter AND inline-expands that
   * summary on first mount; subsequent filter clicks behave
   * normally (the hint isn't re-applied). */
  initialSummaryId?: number | null;
  /** Phase 2.2: the bell's per-item deep-links. A notification for a
   * note or clip opens that item's player modal directly, rather than
   * dropping the reader on the feed to find it again. Applied once per
   * distinct value via `useDeepLinkTarget`, so a re-render can't yank
   * someone back to an item they have moved on from. */
  deepLinkNoteId?: number | null;
  deepLinkClipId?: number | null;
};

export function FeedbackReviewTab({
  bundle,
  reviewedNoteIds,
  reviewedPlaylistIds,
  reviewedClipIds,
  reviewedSummaryIds,
  initialSummaryId,
  deepLinkNoteId,
  deepLinkClipId,
}: FeedbackReviewTabProps) {
  const items = useMemo(
    () =>
      buildFeedbackReviewItems({
        bundle,
        reviewedNoteIds,
        reviewedClipIds,
        reviewedSummaryIds,
      }),
    [bundle, reviewedNoteIds, reviewedClipIds, reviewedSummaryIds],
  );
  // PR 6 — derive the {note,clip,playlist}-id → {signal,note} map from the
  // bundle's `reviews[]` so each card can show the player's current
  // response (or the chip row if none yet).
  const responseIndex = useMemo(
    () => buildPlayerResponseIndex(bundle?.reviews ?? []),
    [bundle?.reviews],
  );

  // PR 2 deep-link: a non-null `initialSummaryId` seeds the filter
  // chip + the expanded card on first mount. We use a lazy initial
  // state so a parent re-render with the same hint doesn't fight a
  // user who's since clicked a different chip. The hint is consumed
  // on mount — the URL keeps the param (back-button stays accurate)
  // but the tab doesn't re-apply it on every render.
  const collection = useCollection(items, {
    scope: `family-review:${bundle?.players.map((p) => p.id).join(":")}`,
    text: (i) => `${i.title} ${i.subtitle} ${i.matchLabel ?? ""}`,
    matches: (i, f) => filterReviewItem(i, f as FeedbackReviewFilter),
  });
  const filter = (collection.filter || "all") as FeedbackReviewFilter;
  const setFilter = (value: FeedbackReviewFilter) =>
    collection.setFilter(value === "all" ? "" : value);
  const filtered = collection.items;

  // Per-filter counts on the chip — `all` shows total, `new` shows
  // pending; the rest are computed once per items snapshot.
  const counts = useMemo(() => {
    const out: Record<FeedbackReviewFilter, number> = {
      all: items.length,
      new: 0,
      video: 0,
      tactical: 0,
      summaries: 0,
      reviewed: 0,
    };
    for (const f of FEEDBACK_REVIEW_FILTERS) {
      if (f === "all") continue;
      out[f] = items.filter((i) => filterReviewItem(i, f)).length;
    }
    return out;
  }, [items]);

  const [openNote, setOpenNote] = useState<CoachingNote | null>(null);
  const [openClip, setOpenClip] = useState<CoachingClip | null>(null);
  const [openPlaylist, setOpenPlaylist] = useState<CoachingPlaylist | null>(
    null,
  );
  const notesById = useMemo(
    () => new Map((bundle?.notes ?? []).map((n) => [n.id, n])),
    [bundle?.notes],
  );
  const clipsById = useMemo(
    () => new Map((bundle?.clips ?? []).map((c) => [c.id, c])),
    [bundle?.clips],
  );
  const playlistsById = useMemo(
    () => new Map((bundle?.playlists ?? []).map((p) => [p.id, p])),
    [bundle?.playlists],
  );
  // Lazy-seeded from the same `?summary=<id>` deep-link as the
  // filter — so the user lands on the Review tab with the matching
  // recap card already inline-expanded. Toggling the filter or
  // clicking another summary follows the normal `expandedSummaryId`
  // state path.
  const [expandedSummaryId, setExpandedSummaryId] = useState<number | null>(
    () => initialSummaryId ?? null,
  );

  // Phase 2.2 — apply the bell's per-item deep-links.
  //
  // The `?summary=` hint above stays a lazy initializer because it only
  // ever arrives on a fresh mount from the Overview tab. The bell is
  // different: it fires while this tab may already be open, where a
  // mount-only read is inert. `useDeepLinkTarget` returns a value only
  // the first time it is seen, so a fresh notification opens its item
  // while a background refetch re-rendering the same URL does not drag
  // the reader back to it.
  const newNoteTarget = useDeepLinkTarget(deepLinkNoteId);
  const newClipTarget = useDeepLinkTarget(deepLinkClipId);
  const newSummaryTarget = useDeepLinkTarget(initialSummaryId);

  useEffect(() => {
    if (newNoteTarget == null) return;
    const note = notesById.get(newNoteTarget);
    // A deep-link to something the viewer can't see (revoked, deleted,
    // or never theirs) must land quietly on the feed rather than
    // erroring — the bundle is already visibility-filtered server-side.
    if (note) setOpenNote(note);
  }, [newNoteTarget, notesById]);

  useEffect(() => {
    if (newClipTarget == null) return;
    const clip = clipsById.get(newClipTarget);
    if (clip) setOpenClip(clip);
  }, [newClipTarget, clipsById]);

  useEffect(() => {
    if (newSummaryTarget == null) return;
    // Recaps expand inline rather than opening a modal, and the tab
    // filters to Summaries so the card is actually on screen.
    collection.reveal(
      (item) =>
        item.source.kind === "summary" &&
        item.source.summary.id === newSummaryTarget,
      "summaries",
    );
    setExpandedSummaryId(newSummaryTarget);
  }, [newSummaryTarget]);

  if (items.length === 0) {
    return (
      <p
        className="text-ink-muted text-sm px-3 py-6"
        data-testid="feedback-review-empty"
      >
        Nothing to review yet. When your coach shares a clip, note, or match
        recap, it will appear here.
      </p>
    );
  }

  function openItem(item: FeedbackReviewItem) {
    if (item.source.kind === "note") setOpenNote(item.source.note);
    else if (item.source.kind === "clip") setOpenClip(item.source.clip);
    else if (item.source.kind === "summary") {
      const id = item.source.summary.id;
      setExpandedSummaryId((cur) => (cur === id ? null : id));
    }
  }

  return (
    /* `max-w-3xl` (~48rem / 768px after padding) caps each card's
     * content column so wide viewports don't stretch the row into
     * unreadable territory with empty space on the right — the
     * mockup itself ships at a 1280px reading-width column with
     * its own centered cards. Centered via `mx-auto` so the feed
     * sits beneath the tab strip naturally; the surrounding
     * `<FeedbackShell>` padding handles the page-edge gutter. */
    <div
      className="flex flex-col gap-4 w-full max-w-3xl mx-auto"
      data-testid="feedback-review"
    >
      <CollectionToolbar collection={collection} label="feedback" />
      {collection.controls ? (
        <FilterChipStrip active={filter} counts={counts} onSelect={setFilter} />
      ) : null}

      {filtered.length === 0 ? (
        <p
          className="text-ink-muted text-sm px-3 py-6"
          data-testid="feedback-review-filter-empty"
        >
          Nothing in this filter. Try another chip above.
        </p>
      ) : (
        <ul className="flex flex-col gap-3" data-testid="feedback-review-list">
          {filtered.map((item) => {
            const response =
              item.source.kind === "note"
                ? responseIndex.forNote(item.source.note.id)
                : item.source.kind === "clip"
                  ? responseIndex.forClip(item.source.clip.id)
                  : // Recaps take no response chips; the empty entry
                    // keeps the shape uniform for the card props.
                    { signal: null, note: "", acknowledgedAt: null };
            return (
              <ReviewCard
                key={item.id}
                item={item}
                expandedSummaryId={expandedSummaryId}
                onOpen={() => openItem(item)}
                responseSignal={response.signal}
                responseNote={response.note}
                responseAcknowledgedAt={response.acknowledgedAt}
                summaryEvidence={{
                  notesById,
                  clipsById,
                  playlistsById,
                  onOpenNote: setOpenNote,
                  onOpenClip: setOpenClip,
                  onOpenPlaylist: setOpenPlaylist,
                }}
              />
            );
          })}
        </ul>
      )}

      <CollectionPagination collection={collection} />
      {openNote ? (
        <FeedbackPlayerModal
          target={{ kind: "note", note: openNote }}
          match={{ id: openNote.match_id ?? "", team_id: openNote.team_id }}
          onClose={() => setOpenNote(null)}
          players={bundle?.players}
          reviewedNoteIds={reviewedNoteIds}
          playerResponses={responseIndex}
        />
      ) : null}
      {openClip ? (
        <FeedbackPlayerModal
          target={{ kind: "clip", clip: openClip }}
          match={{ id: openClip.match_id ?? "", team_id: openClip.team_id }}
          onClose={() => setOpenClip(null)}
          players={bundle?.players}
          reviewedNoteIds={reviewedNoteIds}
          reviewedClipIds={reviewedClipIds}
          playerResponses={responseIndex}
        />
      ) : null}
      {openPlaylist ? (
        <FeedbackPlayerModal
          target={{ kind: "playlist", playlist: openPlaylist }}
          match={{
            id: openPlaylist.items?.[0]?.match_id ?? "",
            team_id: openPlaylist.items?.[0]?.team_id ?? openPlaylist.team_id,
          }}
          onClose={() => setOpenPlaylist(null)}
          players={bundle?.players}
          reviewedNoteIds={reviewedNoteIds}
          reviewedPlaylistIds={reviewedPlaylistIds}
          playerResponses={responseIndex}
        />
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Filter chip strip
// ---------------------------------------------------------------------------

function FilterChipStrip({
  active,
  counts,
  onSelect,
}: {
  active: FeedbackReviewFilter;
  counts: Record<FeedbackReviewFilter, number>;
  onSelect: (f: FeedbackReviewFilter) => void;
}) {
  return (
    <div
      role="tablist"
      aria-label="Filter review items"
      data-testid="feedback-review-filters"
      className="flex gap-1.5 overflow-x-auto rounded border border-rule bg-surface px-2 py-1.5 shadow-sm"
    >
      {FEEDBACK_REVIEW_FILTERS.map((f) => {
        const isActive = active === f;
        const count = counts[f];
        return (
          <button
            key={f}
            type="button"
            role="tab"
            aria-selected={isActive}
            data-feedback-filter={f}
            data-active={isActive}
            onClick={() => onSelect(f)}
            className={[
              "shrink-0 rounded px-3 py-1 text-xs font-display font-semibold uppercase tracking-wide transition-colors",
              isActive
                ? "bg-accent text-accent-ink"
                : "border border-rule text-ink-muted hover:bg-field hover:text-ink",
            ].join(" ")}
          >
            {FEEDBACK_REVIEW_FILTER_LABELS[f]}
            {count > 0 ? (
              <span
                className={[
                  "ml-1.5 text-[0.6rem] font-mono",
                  isActive ? "text-accent-ink" : "text-ink-muted",
                ].join(" ")}
              >
                {count}
              </span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Card
// ---------------------------------------------------------------------------

function ReviewCard({
  item,
  expandedSummaryId,
  onOpen,
  responseSignal,
  responseNote,
  responseAcknowledgedAt,
  summaryEvidence,
}: {
  item: FeedbackReviewItem;
  expandedSummaryId: number | null;
  onOpen: () => void;
  /** PR 6: the player's response signal for this item (or null when
   * they haven't responded yet). Drives the response-chip footer on
   * note and clip cards; summaries omit the chips. */
  responseSignal: import("./responseSignals").PlayerResponseSignal | null;
  responseNote: string;
  responseAcknowledgedAt: string | null;
  summaryEvidence: SummaryEvidenceContext;
}) {
  const review = useFeedbackReview();
  const toast = useToast();

  const isSummary = item.kind === "summary";
  const isExpanded =
    isSummary &&
    item.source.kind === "summary" &&
    expandedSummaryId === item.source.summary.id;

  const dateLabel = item.dateIso ? shortDateLabel(item.dateIso) : "";
  const metaParts: string[] = [];
  if (item.matchLabel) metaParts.push(item.matchLabel);
  if (dateLabel && !item.matchLabel?.includes(dateLabel))
    metaParts.push(dateLabel);

  const actionLabel = primaryActionLabel(item.primaryAction);
  // Recaps read "Mark read" (they're context to absorb, not a task to
  // action); notes + clips keep "Mark done". `isSummary` is declared
  // above (drives the inline-expand behavior too).
  const reviewedActionLabel =
    item.status === "reviewed"
      ? isSummary
        ? "Read ✓"
        : "Done ✓"
      : isSummary
        ? "Mark read"
        : "Mark done";

  // Phase B: mark-reviewed is now meaningful for notes, clips, AND
  // match recaps (each has a review-row target).
  const canMarkReviewed =
    (item.kind === "note" || item.kind === "clip" || item.kind === "summary") &&
    item.status !== "reviewed";
  const alreadyReviewed = item.status === "reviewed";

  async function markDone() {
    try {
      if (item.kind === "note" && item.source.kind === "note") {
        await review.markReviewed({ note_id: item.source.note.id });
      } else if (item.kind === "clip" && item.source.kind === "clip") {
        await review.markReviewed({ clip_id: item.source.clip.id });
      } else if (item.kind === "summary" && item.source.kind === "summary") {
        await review.markReviewed({ summary_id: item.source.summary.id });
      }
      toast.success(isSummary ? "Marked read." : "Marked done.");
    } catch (err) {
      toast.error(errorMessage(err) || "Couldn't mark done.");
    }
  }

  return (
    <li>
      <article
        data-testid="feedback-review-card"
        data-review-item-kind={item.kind}
        data-review-status={item.status}
        data-review-tone={item.tone}
        className="flex flex-col gap-3 rounded border border-rule bg-surface p-3 shadow-sm sm:flex-row sm:items-stretch"
      >
        {/* Per-kind thumb — video notes + clips use the auth-fetched
         * JPEG (`useThumbnail`), tactical observations show a mini
         * board preview, plain observations a clipboard glyph,
         * summaries a score chip.
         * Sized to a 16:9 envelope (~144px wide on desktop / full
         * width on mobile) so it carries weight against the title
         * column without leaving the dead space the small per-tab
         * tile had. */}
        <ReviewCardThumb item={item} />
        {/* Right-hand column: pills, title, meta, subtitle, actions,
         * response chips. One vertical stack so the thumb's
         * neighboring whitespace gets used by content as the card
         * grows — fixes the dead-space layout flagged in review. */}
        <div className="min-w-0 flex-1 flex flex-col gap-2">
          <div className="flex flex-wrap items-center gap-2">
            <TypePill label={item.typeLabel} />
            <StatusPill status={item.status} />
            {item.tone !== "neutral" ? <TonePill tone={item.tone} /> : null}
          </div>
          <h4
            className="font-display text-base text-ink"
            data-testid="feedback-review-card-title"
          >
            {item.title}
          </h4>
          {metaParts.length > 0 ? (
            <p
              className="text-ink-muted text-[0.7rem] font-mono truncate"
              data-testid="feedback-review-card-meta"
            >
              {metaParts.join(" · ")}
            </p>
          ) : null}
          <p
            className="text-sm text-ink-muted line-clamp-2"
            data-testid="feedback-review-card-subtitle"
          >
            {item.primaryAction === "watch" ||
            item.primaryAction === "review" ? (
              <>
                <span className="font-display text-ink">
                  What to do next —{" "}
                </span>
                {item.subtitle}
              </>
            ) : (
              item.subtitle
            )}
          </p>

          {isExpanded && item.source.kind === "summary" ? (
            <SummaryExpanded
              summary={item.source.summary}
              evidence={summaryEvidence}
            />
          ) : null}

          {/* Action row pushed to the bottom of the column so the
           * primary CTA aligns to the thumb's lower edge on tall
           * cards — keeps the visual weight balanced. */}
          <div className="mt-auto flex flex-wrap items-center gap-2 pt-1">
            <Button
              variant="primary"
              size="sm"
              onClick={onOpen}
              data-testid="feedback-review-card-action"
            >
              {actionLabel}
            </Button>
            {canMarkReviewed ? (
              <Button
                variant="outline"
                size="sm"
                onClick={markDone}
                disabled={review.isPending}
                data-testid="feedback-review-card-mark-done"
              >
                {review.isPending
                  ? "Marking…"
                  : isSummary
                    ? "Mark read"
                    : "Mark done"}
              </Button>
            ) : alreadyReviewed ? (
              <span
                className="text-xs font-display uppercase tracking-wider text-ink-muted"
                data-testid="feedback-review-card-already-reviewed"
              >
                {reviewedActionLabel}
              </span>
            ) : null}
          </div>
          {/* Got it / Need help / I tried this. PR 6 launched on
           * notes; PR 7 extended to clips once the backend accepted
           * `clip_id` reviews. Recaps are intentionally read-only —
           * they're team-wide context, not a player-targeted task — so
           * they get "Mark read" only, no response signals (the
           * /api/my-feedback/review handler rejects a signal on a
           * summary_id). */}
          {item.source.kind === "note" ? (
            <div className="mt-3">
              <PlayerResponseChips
                target={{ kind: "note", noteId: item.source.note.id }}
                currentSignal={responseSignal}
                currentNote={responseNote}
                acknowledgedAt={responseAcknowledgedAt}
              />
            </div>
          ) : item.source.kind === "clip" ? (
            <div className="mt-3">
              <PlayerResponseChips
                target={{ kind: "clip", clipId: item.source.clip.id }}
                currentSignal={responseSignal}
                currentNote={responseNote}
                acknowledgedAt={responseAcknowledgedAt}
              />
            </div>
          ) : null}
        </div>
      </article>
    </li>
  );
}

// ---------------------------------------------------------------------------
// Inline summary expansion
// ---------------------------------------------------------------------------

type SummaryEvidenceContext = {
  notesById: Map<number, CoachingNote>;
  clipsById: Map<number, CoachingClip>;
  playlistsById: Map<number, CoachingPlaylist>;
  onOpenNote: (note: CoachingNote) => void;
  onOpenClip: (clip: CoachingClip) => void;
  onOpenPlaylist: (playlist: CoachingPlaylist) => void;
};

function SummaryExpanded({
  summary,
  evidence,
}: {
  summary: MatchSummary;
  evidence: SummaryEvidenceContext;
}) {
  const sections = summarySections(summary);
  const linkedNotes = (summary.note_ids ?? [])
    .map((id) => evidence.notesById.get(id))
    .filter((note): note is CoachingNote => !!note);
  const linkedClips = (summary.clip_ids ?? [])
    .map((id) => evidence.clipsById.get(id))
    .filter((clip): clip is CoachingClip => !!clip);
  const linkedPlaylists = (summary.playlist_ids ?? [])
    .map((id) => evidence.playlistsById.get(id))
    .filter((playlist): playlist is CoachingPlaylist => !!playlist);
  const sourceCount =
    linkedNotes.length + linkedClips.length + linkedPlaylists.length;
  if (sections.length === 0 && sourceCount === 0 && !summary.visibility) {
    return null;
  }
  return (
    <div
      className="mt-3 flex flex-col gap-2 rounded border border-rule bg-surface p-3"
      data-testid="feedback-review-card-summary-expanded"
    >
      <div
        className="flex flex-wrap items-center gap-1.5 text-xs text-ink-muted"
        data-testid="feedback-summary-meta"
      >
        {summary.visibility ? (
          <span
            data-testid="feedback-summary-visibility"
            className="rounded border border-rule px-1.5 py-0.5 text-[0.6rem] font-display uppercase tracking-wider text-ink"
          >
            {summaryVisibilityLabel(summary.visibility)}
          </span>
        ) : null}
        <span>
          {sourceCount > 0
            ? `${sourceCount} source${sourceCount === 1 ? "" : "s"} linked`
            : "Team recap"}
        </span>
      </div>
      {sections.map(([label, value]) => (
        <div key={label}>
          <p className="text-[0.65rem] font-display uppercase tracking-wider text-ink-muted">
            {label}
          </p>
          <p className="text-sm text-ink mt-1">{value}</p>
        </div>
      ))}
      {sourceCount > 0 ? (
        <div
          className="flex flex-wrap gap-1.5 pt-1"
          data-testid="feedback-summary-sources"
          aria-label="Linked evidence"
        >
          {linkedNotes.map((note) => (
            <EvidenceButton
              key={`note-${note.id}`}
              label={`Note: ${noteDisplayTitle(note)}`}
              onClick={() => evidence.onOpenNote(note)}
            />
          ))}
          {linkedClips.map((clip) => (
            <EvidenceButton
              key={`clip-${clip.id}`}
              label={`Clip: ${clip.title?.trim() || "Clip"}`}
              onClick={() => evidence.onOpenClip(clip)}
            />
          ))}
          {linkedPlaylists.map((playlist) => (
            <EvidenceButton
              key={`playlist-${playlist.id}`}
              label={`Legacy session: ${playlist.title?.trim() || "Session"}`}
              onClick={() => evidence.onOpenPlaylist(playlist)}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}

function EvidenceButton({
  label,
  onClick,
}: {
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid="feedback-summary-source-button"
      className="rounded border border-rule px-2.5 py-1 text-xs font-display text-ink-muted transition-colors hover:bg-field hover:text-ink"
    >
      {label}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Pills
// ---------------------------------------------------------------------------

function TypePill({ label }: { label: string }) {
  return (
    <Badge variant="outline" data-testid="feedback-review-card-type-pill">
      {label}
    </Badge>
  );
}

function StatusPill({ status }: { status: FeedbackReviewItem["status"] }) {
  if (status === "reviewed") {
    return (
      <Badge
        variant="muted"
        data-testid="feedback-review-card-status-pill"
        data-status="reviewed"
      >
        Reviewed
      </Badge>
    );
  }
  if (status === "new") {
    return (
      <Badge
        variant="accent"
        data-testid="feedback-review-card-status-pill"
        data-status="new"
      >
        New
      </Badge>
    );
  }
  return (
    <Badge
      variant="muted"
      data-testid="feedback-review-card-status-pill"
      data-status="untracked"
    >
      Review
    </Badge>
  );
}

function TonePill({ tone }: { tone: FeedbackReviewItem["tone"] }) {
  const label =
    tone === "positive"
      ? "Positive"
      : tone === "correction"
        ? "To work on"
        : tone === "goal"
          ? "Goal"
          : "Team concept";
  const variant =
    tone === "positive"
      ? "good"
      : tone === "correction"
        ? "flag"
        : tone === "goal"
          ? "accent"
          : "muted";
  return (
    <Badge
      variant={variant}
      data-testid="feedback-review-card-tone-pill"
      data-tone={tone}
    >
      {label}
    </Badge>
  );
}

// ---------------------------------------------------------------------------
// Action label
// ---------------------------------------------------------------------------

function primaryActionLabel(
  action: FeedbackReviewItem["primaryAction"],
): string {
  switch (action) {
    case "watch":
      return "Watch";
    case "review":
      return "Review";
    case "read":
      return "Read";
  }
}

// ---------------------------------------------------------------------------
// Per-kind thumb chooser
// ---------------------------------------------------------------------------

/** Card-thumb envelope — 16:9 box sized for visual weight against
 * the title column.
 *
 * Sizing reasoning:
 *   - mobile: full width, capped by the article's padding (the
 *     parent's `flex-col` stacks the thumb above the content, so
 *     it gets the whole card width; the 16:9 ratio keeps it from
 *     dominating the viewport)
 *   - >=sm: fixed 11rem (≈ 176px) wide so it leaves room for the
 *     title block at 768px and looks proportional next to the
 *     primary CTA at desktop. Larger than the legacy 80×48 tile,
 *     not so large that two cards stop fitting on a phone.
 *
 * The envelope sets a 16:9 aspect-ratio so the JPEG / board / chip
 * content inside fills consistently regardless of source.
 */
// `self-start` keeps the thumb's height pinned to the 16:9 aspect
// ratio instead of stretching to match the article's
// `items-stretch` flex container — without it the thumb height
// followed the title-column's content height, leaving a tall thin
// box. `sm:self-start` is the version that takes effect at the
// row layout breakpoint; on mobile (column layout) the thumb
// renders above the content naturally.
const CARD_THUMB_ENVELOPE =
  "block w-full sm:w-44 sm:self-start shrink-0 aspect-video overflow-hidden rounded border border-rule/60 bg-gradient-to-br from-canvas to-surface";

/** Pick the right per-card preview given the item's source kind +
 * (for notes) the observation/board state. Restores the per-card
 * thumb the legacy `FeedbackNotesTab` / `FeedbackClipsTab` /
 * legacy object-specific cards each had,
 * but rendered at hero-card scale (16:9, ~176px wide) so the card
 * doesn't leave dead space next to the thumb anymore. */
function ReviewCardThumb({ item }: { item: FeedbackReviewItem }) {
  if (item.source.kind === "note") {
    const note = item.source.note;
    const isObservation = (note.note_context ?? "video") === "observation";
    if (isObservation) {
      if (boardHasContent(note.tactical_board_json)) {
        return <BoardThumb raw={note.tactical_board_json} />;
      }
      return <ObservationGlyphThumb />;
    }
    return <CardJpegThumb kind="note" id={note.id} />;
  }
  if (item.source.kind === "clip") {
    return <CardJpegThumb kind="clip" id={item.source.clip.id} />;
  }
  // summary
  return <SummaryThumb />;
}

/** Tactical-board preview at card-thumb scale — uses the shared
 * tactical thumbnail primitive at the 176px-wide envelope so the
 * pitch is legible. */
function BoardThumb({ raw }: { raw: unknown }) {
  // Hard-code width to match the >=sm envelope (w-44 = 11rem ≈ 176
  // px). On mobile the envelope is full-width; the SVG scales down
  // visually via the surrounding `aspect-video` clip. The pitch
  // aspect (~1.544:1) is narrower than 16:9 (~1.778:1), so a bit
  // of horizontal padding appears on the sides — the same way the
  // FeedbackPlayerModal renders an observation note's board.
  return (
    <TacticalBoardThumbnail
      raw={raw}
      width={176}
      fallback="none"
      testId="feedback-review-thumb-board"
      className={`${CARD_THUMB_ENVELOPE} flex items-center justify-center bg-surface`}
    />
  );
}

/** Plain observation thumb — a centered clipboard glyph filling
 * the 16:9 envelope. Larger than the legacy 80×48 tile so it
 * doesn't look anemic next to the title block. */
function ObservationGlyphThumb() {
  return (
    <span
      className={`${CARD_THUMB_ENVELOPE} flex items-center justify-center bg-surface text-ink-muted`}
      aria-hidden="true"
      data-testid="feedback-review-thumb-observation"
    >
      <ClipboardList size={32} strokeWidth={1.75} aria-hidden="true" />
    </span>
  );
}

/** Card-scale variant of `FeedbackThumb` — same auth-fetched JPEG
 * blob via `useThumbnail`, filling the 16:9 envelope instead of
 * the small per-tab 80×48 tile. */
function CardJpegThumb({ kind, id }: { kind: "note" | "clip"; id: number }) {
  return (
    <FeedbackMediaThumbnail
      kind={kind}
      id={id}
      testId="feedback-review-thumb"
      placeholderTestId="feedback-review-thumb-placeholder"
      className={CARD_THUMB_ENVELOPE}
    />
  );
}

/** Match-summary "thumb" — the mockup shows a navy card with the
 * score; we don't carry final scores on the bundle today, so a
 * neutral navy `RECAP` chip stands in. Slot is ready for a score
 * render once the bundle exposes one. */
function SummaryThumb() {
  return (
    <span
      className={`${CARD_THUMB_ENVELOPE} flex items-center justify-center bg-ink text-ink-inverse font-display text-sm uppercase tracking-wider`}
      aria-hidden="true"
      data-testid="feedback-review-thumb-summary"
    >
      Recap
    </span>
  );
}
